import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../design/Icon';
import { Alert } from '../../components/shared/Feedback';
import { SyntheticDataBadge } from '../../components/shared/Badges';
import { EmptyState } from '../../components/shared/States';
import { Dialog, MenuTitle, Popover, usePopover, useToast } from '../../components/shared/Overlay';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { errorMessage, reportsAPI } from '../../services/reportsApi';
import { FilterChips } from './FilterChips';
import { cadenceText, pickerOrder, previewCell, splitColumns } from './reportsLogic';

// Dataset cards in the mockup's order and words; other datasets appear only for a saved report that uses them.
const PRIMARY = [
  { dataset: 'Calls', title: 'Calls', sub: 'One row per call' },
  { dataset: 'CallJourneyHops', title: 'Call route segments', sub: 'One row per step' },
  { dataset: 'Classifications', title: 'Classifications', sub: 'One row per label' },
  { dataset: 'Agents', title: 'Agents', sub: 'Per agent' },
  { dataset: 'Queues', title: 'Queues', sub: 'Per queue' },
];
const PREVIEW_ROWS = 6;
const NUMERIC = ['Integer', 'Decimal', 'Percentage', 'Duration'];
const EXPORT_FORMATS = [
  { format: 'csv', label: 'CSV', description: 'Comma-separated values (.csv)' },
  { format: 'xlsx', label: 'Excel workbook', description: 'Formatted workbook with filters applied (.xlsx)' },
  { format: 'pdf', label: 'PDF', description: 'Printable table (.pdf)' },
];

const defaultsOf = (descriptor) => (descriptor ? descriptor.columns.filter((c) => c.defaultSelected).map((c) => c.key) : []);

function blankReport(descriptor) {
  const columns = defaultsOf(descriptor);
  return {
    name: 'Untitled report',
    dataset: descriptor?.dataset || 'Calls',
    template: 'Table',
    columns,
    filter: {},
    dateRange: { preset: 'Last30Days' },
    sortColumn: columns.includes('startTime') ? 'startTime' : null,
    sortDescending: true,
    isShared: true,
  };
}

function toDraft(report, descriptor) {
  return {
    ...report,
    columns: report.columns && report.columns.length ? report.columns : defaultsOf(descriptor),
    filter: report.filter || {},
    dateRange: report.dateRange || { preset: 'Last30Days' },
  };
}

function savedSubtitle(report, datasetNames, schedules) {
  const own = (schedules || []).filter((s) => s.savedReportId === report.id);
  const what =
    report.template === 'CommunityMonthlySummary'
      ? 'Board summary'
      : [datasetNames[report.dataset] || report.dataset, report.columns?.length ? `${report.columns.length} columns` : null].filter(Boolean).join(' · ');
  return `${what} · ${cadenceText(own)}`;
}

const byRecent = (a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')) || a.name.localeCompare(b.name);

export function BuilderTab({ datasets, saved: savedReports, schedules, onSavedChanged, onSchedule, canManage = true, canExport = true }) {
  const saved = useMemo(() => (savedReports ? [...savedReports].sort(byRecent) : null), [savedReports]);
  const toast = useToast();
  const hierarchy = useHierarchyFilter();
  const exportMenu = usePopover();
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [categories, setCategories] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const dragKey = useRef(null);

  const byDataset = useMemo(() => Object.fromEntries((datasets || []).map((d) => [d.dataset, d])), [datasets]);
  const datasetNames = useMemo(() => Object.fromEntries((datasets || []).map((d) => [d.dataset, d.displayName])), [datasets]);
  const descriptor = draft ? byDataset[draft.dataset] : null;
  const pickerColumns = useMemo(() => splitColumns(draft?.dataset, descriptor?.columns || []), [draft?.dataset, descriptor]);

  useEffect(() => {
    reportsAPI.getActiveTaxonomy().then(
      (taxonomy) => setCategories((taxonomy?.categories || []).map((c) => ({ value: c.id, label: c.name }))),
      () => setCategories(null)
    );
  }, []);

  useEffect(() => {
    if (!saved || !datasets || draft) return;
    if (saved.length) {
      setSelectedId(saved[0].id);
      setDraft(toDraft(saved[0], byDataset[saved[0].dataset]));
    }
  }, [saved, datasets, draft, byDataset]);

  const isBoard = draft?.template === 'CommunityMonthlySummary';

  useEffect(() => {
    if (!draft || !descriptor || isBoard) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      reportsAPI
        .preview({
          dataset: draft.dataset,
          columns: draft.columns,
          filter: draft.filter,
          dateRange: draft.dateRange,
          sortColumn: draft.sortColumn || undefined,
          sortDescending: draft.sortDescending,
          limit: PREVIEW_ROWS,
        })
        .then(
          (result) => {
            if (cancelled) return;
            setPreview(result);
            setPreviewError(null);
          },
          async (error) => {
            if (!cancelled) setPreviewError(await errorMessage(error, "The preview couldn't load"));
          }
        );
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [draft, descriptor, isBoard]);

  if (!datasets || !saved) return <div className="card is-loading"><div className="card-body" style={{ minHeight: 320 }} /></div>;

  if (!draft) {
    return (
      <EmptyState
        icon="file"
        title="No saved reports yet"
        body="Build a report from calls, routes, agents, queues, or classifications, then save it to reuse or schedule."
        actions={
          canManage && (
            <button type="button" className="btn btn-primary" onClick={() => setDraft(blankReport(byDataset.Calls || datasets[0]))}>
              Build a report
            </button>
          )
        }
      />
    );
  }

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const select = (report) => {
    setSelectedId(report.id);
    setPreview(null);
    setDraft(toDraft(report, byDataset[report.dataset]));
  };
  const startNew = () => {
    setSelectedId(null);
    setPreview(null);
    setDraft(blankReport(byDataset.Calls || datasets[0]));
  };

  const chooseDataset = (dataset) => {
    const next = byDataset[dataset];
    const columns = defaultsOf(next);
    setPreview(null);
    update({ dataset, columns, sortColumn: columns.includes(draft.sortColumn) ? draft.sortColumn : columns.includes('startTime') ? 'startTime' : null });
  };

  const toggleColumn = (key) => {
    const order = pickerOrder(draft.dataset, descriptor.columns);
    const has = draft.columns.includes(key);
    const columns = has ? draft.columns.filter((k) => k !== key) : [...draft.columns, key].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    update({ columns });
  };

  const dropOn = (targetKey) => {
    const from = dragKey.current;
    dragKey.current = null;
    if (!from || from === targetKey) return;
    const columns = draft.columns.filter((k) => k !== from);
    columns.splice(columns.indexOf(targetKey), 0, from);
    update({ columns });
  };

  const body = () => ({
    name: draft.name.trim(),
    description: draft.description || null,
    dataset: draft.dataset,
    template: draft.template || 'Table',
    columns: draft.columns,
    filter: draft.filter,
    dateRange: draft.dateRange,
    sortColumn: draft.sortColumn || null,
    sortDescending: Boolean(draft.sortDescending),
    isShared: draft.isShared !== false,
  });

  const save = async () => {
    if (!draft.name.trim()) {
      toast('Give the report a name first', 'alert');
      return;
    }
    setSaving(true);
    try {
      const stored = selectedId ? await reportsAPI.updateSaved(selectedId, body()) : await reportsAPI.createSaved(body());
      setSelectedId(stored.id);
      setDraft(toDraft(stored, byDataset[stored.dataset]));
      toast(`Saved “${stored.name}”`);
      onSavedChanged();
    } catch (error) {
      toast(await errorMessage(error, "The report couldn't be saved"), 'alert');
    } finally {
      setSaving(false);
    }
  };

  const exportAs = async (format) => {
    exportMenu.close();
    const result = isBoard && selectedId ? await reportsAPI.runSaved(selectedId, format) : await reportsAPI.run({ ...body(), title: draft.name.trim() || undefined }, format);
    toast(result.success ? `Exported ${result.filename}` : result.error, result.success ? 'checkCircle' : 'alert');
  };

  const remove = async (report) => {
    setDeleting(null);
    try {
      await reportsAPI.deleteSaved(report.id);
      toast(`Deleted “${report.name}”`);
      setSelectedId(null);
      setPreview(null);
      setDraft(null);
      onSavedChanged();
    } catch (error) {
      toast(await errorMessage(error, "The report couldn't be deleted"), 'alert');
    }
  };

  const schedule = () => {
    const report = saved.find((r) => r.id === selectedId);
    if (!report) {
      toast('Save the report before scheduling it', 'alert');
      return;
    }
    onSchedule(report);
  };

  const cards = PRIMARY.filter((p) => byDataset[p.dataset]);
  if (byDataset[draft.dataset] && !cards.some((c) => c.dataset === draft.dataset)) {
    cards.push({ dataset: draft.dataset, title: byDataset[draft.dataset].displayName, sub: byDataset[draft.dataset].description });
  }
  const columnsByKey = Object.fromEntries((descriptor?.columns || []).map((c) => [c.key, c]));
  const previewColumns = preview?.columns || [];

  return (
    <div className="builder">
      <aside className="card">
        <div className="card-head">
          <div className="t">Saved reports</div>
          <div className="acts">
            {canManage && (
              <button type="button" className="btn btn-sm btn-primary" onClick={startNew}>
                <Icon name="plus" className="ico-sm" />
                New
              </button>
            )}
          </div>
        </div>
        <div className="card-body flush">
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {saved.map((report) => (
              <li key={report.id} style={{ padding: '10px 16px', borderTop: '1px solid var(--divider)', background: report.id === selectedId ? 'var(--accent-soft)' : undefined, cursor: 'pointer' }}>
                <button type="button" onClick={() => select(report)} style={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer' }}>
                  <div className="small strong">{report.name}</div>
                  <div className="caption">{savedSubtitle(report, datasetNames, schedules)}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <section className="card">
        <div className="card-head" style={{ alignItems: 'center' }}>
          <div className="grow">
            <label className="sr-only" htmlFor="rn">
              Report name
            </label>
            <input className="input" id="rn" value={draft.name} onChange={(e) => update({ name: e.target.value })} style={{ fontSize: 'var(--fs-h3)', fontWeight: 600, maxWidth: 380 }} />
          </div>
          <div className="acts">
            {canManage && selectedId && (
              <button
                type="button"
                className="btn btn-ghost icon-btn"
                aria-label="Delete report"
                title="Delete report"
                onClick={() => setDeleting(saved.find((r) => r.id === selectedId) || null)}
              >
                <Icon name="trash" />
              </button>
            )}
            {canManage && (
              <button type="button" className="btn" onClick={schedule}>
                Schedule
              </button>
            )}
            {canExport && (
              <Popover
                open={exportMenu.open}
                onClose={exportMenu.close}
                align="right"
                width={300}
                label={`Export ${draft.name}`}
                anchor={
                  <button type="button" className="btn" id="b-exp" aria-haspopup="true" aria-expanded={exportMenu.open} onClick={exportMenu.toggle}>
                    <Icon name="download" className="ico-sm" />
                    Export
                  </button>
                }
              >
                <MenuTitle>Export {draft.name}</MenuTitle>
                {(isBoard ? EXPORT_FORMATS.filter((f) => f.format !== 'csv') : EXPORT_FORMATS).map((f) => (
                  <button key={f.format} type="button" className="menu-item" onClick={() => exportAs(f.format)}>
                    <Icon name={f.format === 'pdf' ? 'file' : 'download'} />
                    <span>
                      <span style={{ display: 'block' }}>{f.label}</span>
                      <span className="caption">{f.description}</span>
                    </span>
                  </button>
                ))}
              </Popover>
            )}
            {canManage && (
              <button type="button" className="btn btn-primary" id="b-save" onClick={save} disabled={saving || isBoard}>
                Save report
              </button>
            )}
          </div>
        </div>

        <div className="card-body stack-lg">
          {isBoard ? (
            <Alert title="Board summary">
              This saved report is a board summary for {hierarchy.communitiesById[draft.filter?.communityIds?.[0]]?.name || `one ${hierarchy.terms.communityLower}`}. Choose the {hierarchy.terms.communityLower} and month on the Board summary tab; schedule it from here or from Schedules.
            </Alert>
          ) : (
            <>
              <div>
                <div className="bstep">
                  <span className="n">1</span>Dataset
                </div>
                <div className="ds" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
                  {cards.map((card) => (
                    <label key={card.dataset}>
                      <input type="radio" name="ds" checked={draft.dataset === card.dataset} onChange={() => chooseDataset(card.dataset)} />
                      <span>
                        <span className="small strong" style={{ display: 'block' }}>
                          {card.title}
                        </span>
                        <span className="caption">{card.sub}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <div className="bstep">
                  <span className="n">2</span>Columns{' '}
                  <span className="caption" style={{ fontWeight: 400 }}>
                    {draft.columns.length} of {showMore ? descriptor?.columns.length || 0 : pickerColumns.primary.length} selected, drag in preview to reorder
                  </span>
                </div>
                <div className="colpick">
                  {(showMore ? [...pickerColumns.primary, ...pickerColumns.more] : pickerColumns.primary).map((column) => (
                    <label key={column.key}>
                      <input type="checkbox" checked={draft.columns.includes(column.key)} onChange={() => toggleColumn(column.key)} />
                      {column.header}
                    </label>
                  ))}
                </div>
                {pickerColumns.more.length > 0 && (
                  <button type="button" className="btn btn-ghost btn-sm mt-2" aria-expanded={showMore} onClick={() => setShowMore((v) => !v)}>
                    <Icon name={showMore ? 'chevU' : 'chevD'} className="ico-sm" />
                    {showMore ? 'Fewer columns' : `More columns (${pickerColumns.more.length})`}
                  </button>
                )}
              </div>

              <div>
                <div className="bstep">
                  <span className="n">3</span>Filters
                </div>
                <FilterChips
                  report={draft}
                  filterKeys={descriptor?.filters}
                  columns={draft.columns.map((key) => columnsByKey[key]).filter(Boolean)}
                  hierarchy={hierarchy}
                  categories={categories}
                  onChange={update}
                />
              </div>

              <div>
                <div className="bstep">
                  <span className="n">4</span>Preview{' '}
                  <span className="caption" style={{ fontWeight: 400 }}>
                    {preview ? `First ${Math.min(PREVIEW_ROWS, preview.rows.length)} of ${preview.totalRows.toLocaleString('en-US')} rows` : 'Loading…'}
                  </span>{' '}
                  {preview?.includesSyntheticData && <SyntheticDataBadge />}
                </div>
                {previewError ? (
                  <Alert tone="error">{previewError}</Alert>
                ) : (
                  <div className="table-wrap" style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                    <table className="table compact">
                      <thead>
                        <tr>
                          {previewColumns.map((column) => (
                            <th
                              key={column.key}
                              className={NUMERIC.includes(column.type) ? 'n' : undefined}
                              draggable
                              onDragStart={() => {
                                dragKey.current = column.key;
                              }}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => dropOn(column.key)}
                              title="Drag to reorder"
                            >
                              {column.header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview && preview.rows.length === 0 && (
                          <tr>
                            <td colSpan={Math.max(1, previewColumns.length)} className="caption">
                              No rows match these filters and dates.
                            </td>
                          </tr>
                        )}
                        {(preview?.rows || []).map((row, i) => (
                          <tr key={i}>
                            {row.map((value, j) => {
                              const type = previewColumns[j]?.type;
                              const text = previewCell(value, type);
                              return (
                                <td key={j} className={[NUMERIC.includes(type) ? 'n' : '', text.length < 40 ? 'nowrap' : ''].filter(Boolean).join(' ') || undefined}>
                                  {text}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>
      <DeleteReportDialog report={deleting} schedules={schedules} onCancel={() => setDeleting(null)} onDelete={remove} />
    </div>
  );
}

function DeleteReportDialog({ report, schedules, onCancel, onDelete }) {
  const own = report ? (schedules || []).filter((s) => s.savedReportId === report.id) : [];
  return (
    <Dialog
      open={Boolean(report)}
      onClose={onCancel}
      title="Delete report"
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={() => onDelete(report)} disabled={own.length > 0}>
            Delete report
          </button>
        </>
      }
    >
      <p className="small">
        {own.length
          ? `“${report?.name}” has ${own.length === 1 ? 'a schedule' : `${own.length} schedules`}. Delete ${own.length === 1 ? 'it' : 'them'} on the Schedules tab first.`
          : `Delete “${report?.name}”? It is removed for everyone it was shared with. Files already exported are not affected.`}
      </p>
    </Dialog>
  );
}
